import React from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUser,
  faCalendar,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons'
import { useGlobalSearch } from '../../../queries/useGlobalSearch'

interface SearchModalResultsProps {
  queryString: string
  onResultClick?: () => void
  isAdminContext?: boolean
}

export const SearchModalResults: React.FC<SearchModalResultsProps> = ({
  queryString,
  onResultClick,
  isAdminContext = false,
}) => {
  const navigate = useNavigate()
  const { data, isLoading } = useGlobalSearch({
    query: queryString,
    limit: 6,
    isAdminContext,
  })

  const handleNavigate = (path: string) => {
    navigate(path)
    if (onResultClick) {
      onResultClick()
    }
  }

  // Generate navigation paths based on context
  const getProfilePath = (profileId: string) => {
    return isAdminContext
      ? `/admin/profiles/show/${profileId}`
      : `/app/matches/${profileId}`
  }

  const getEventPath = (eventId: string) => {
    return isAdminContext
      ? `/admin/events/show/${eventId}`
      : `/app/events/${eventId}`
  }

  const getProfilesSearchPath = (query: string) => {
    return isAdminContext
      ? `/admin/profiles?search=${encodeURIComponent(query)}`
      : `/app/matches?search=${encodeURIComponent(query)}`
  }

  const getEventsSearchPath = (query: string) => {
    return isAdminContext
      ? `/admin/events?search=${encodeURIComponent(query)}`
      : `/app/events?search=${encodeURIComponent(query)}`
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const profiles = data?.search?.profiles?.nodes || []
  const events = data?.search?.events?.nodes || []
  const hasResults = profiles.length > 0 || events.length > 0

  if (!hasResults) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="mb-4">
          <FontAwesomeIcon icon={faUser} size="2x" className="text-gray-300" />
        </div>
        <p className="text-lg font-medium mb-2">No results found</p>
        <p className="text-sm">Try searching for a different term</p>
      </div>
    )
  }

  return (
    <div className="py-2">
      {/* Profiles Section */}
      {profiles.length > 0 && (
        <div className="mb-4">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
              <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-500" />
              Profiles ({profiles.length})
            </h3>
          </div>
          <div className="py-2">
            {profiles.slice(0, 3).map((profile) => (
              <button
                key={profile.id}
                onClick={() => handleNavigate(getProfilePath(profile.id))}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors group"
              >
                <div className="flex items-center">
                  <div
                    className="w-10 h-10 rounded-full mr-3 flex items-center justify-center text-white font-medium"
                    style={{ backgroundColor: profile.color || '#6B7280' }}
                  >
                    {profile.avatarUrl ? (
                      <img
                        src={profile.avatarUrl}
                        alt={`${profile.firstName} ${profile.lastName}`}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-sm">
                        {profile.firstName?.[0]}
                        {profile.lastName?.[0]}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {profile.firstName} {profile.lastName}
                    </div>
                    <div className="text-sm text-gray-500 truncate">
                      {profile.role === 'EDUCATOR'
                        ? profile.schoolName
                        : profile.companyName}
                      {profile.role && (
                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {profile.role.toLowerCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-gray-400 group-hover:text-gray-600 ml-2"
                    size="sm"
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Events Section */}
      {events.length > 0 && (
        <div>
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
              <FontAwesomeIcon
                icon={faCalendar}
                className="mr-2 text-green-500"
              />
              Events ({events.length})
            </h3>
          </div>
          <div className="py-2">
            {events.slice(0, 3).map((event) => (
              <button
                key={event.id}
                onClick={() => handleNavigate(getEventPath(event.id))}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors group"
              >
                <div className="flex items-center">
                  <div
                    className="w-10 h-10 rounded-lg mr-3 flex items-center justify-center text-white font-medium"
                    style={{ backgroundColor: event.color || '#1E988A' }}
                  >
                    {event.iconUrl ? (
                      <img
                        src={event.iconUrl}
                        alt={event.name}
                        className="w-full h-full rounded-lg object-cover"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faCalendar} size="sm" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {event.name}
                    </div>
                    <div className="text-sm text-gray-500 truncate">
                      {event.description}
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-gray-400 group-hover:text-gray-600 ml-2"
                    size="sm"
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Show all results links */}
      {(profiles.length > 3 || events.length > 3) && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between text-sm">
            {profiles.length > 3 && (
              <button
                onClick={() =>
                  handleNavigate(getProfilesSearchPath(queryString))
                }
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                View all {profiles.length} profiles →
              </button>
            )}
            {events.length > 3 && (
              <button
                onClick={() => handleNavigate(getEventsSearchPath(queryString))}
                className="text-green-600 hover:text-green-800 font-medium"
              >
                View all {events.length} events →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
